import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createClientNote,
  deleteNote,
  fetchClientNotes,
  fetchUnreadCount,
  fetchVisitNotes,
  markNotesRead,
  updateNote,
} from './notes.api';
import { CreateNotePayload, Note, NoteFlagValue, NoteList, NoteRole, UpdateNotePayload } from './notes.types';

/**
 * Query keys for the notes feature.
 *
 * `clientNotes` carries the filter in the key rather than filtering a cached list:
 * the server pages the stream, so a filtered view is a different result set and not
 * a subset of one already held.
 */
export const NOTES_KEYS = {
  clientNotes: 'notes-client',
  visitNotes: 'notes-visit',
  unread: 'notes-unread-count',
} as const;

/** How many notes one page of the client timeline asks for; the panel grows it. */
export const NOTES_PAGE_SIZE = 50;

const EMPTY_LIST: NoteList = { notes: [], total: 0 };

/**
 * Every note on a client, newest first — including the ones written on that client's
 * visits, because the endpoint is anchored to the client and not to a visit.
 *
 * The priority filter is the server's, not this hook's. Filtering a page here would
 * make both numbers a lie: the count would describe one page of an unfiltered stream,
 * and "no notes with this priority" would mean "none in the first hundred".
 *
 * The endpoint takes a single `flag`, so several chosen priorities are several
 * requests. `notes.flag` holds at most one value, so the result sets are disjoint and
 * their totals add up — which is why the total this returns is the server's count of
 * the filter actually in force, whatever `limit` was asked for.
 *
 * `enabled` guards the id the way every other client-scoped query here does: a card
 * mounted on a bad route must not fire `/clients/NaN/notes`.
 */
export const useClientNotes = (
  clientId: number,
  flags: readonly NoteFlagValue[] = [],
  limit: number = NOTES_PAGE_SIZE,
  enabled = true,
  /**
   * Restrict to one author role, server-side. In the key, so the caregiver-only panel
   * and the whole-stream panel are two results rather than one filtered twice — and so
   * the count each reports is the server's count of the question it actually asked.
   */
  authorRole?: NoteRole
) => {
  const isEnabled = enabled && Number.isFinite(clientId) && clientId > 0;
  const filters: Array<NoteFlagValue | null> = flags.length === 0 ? [null] : [...flags];

  const results = useQueries({
    queries: filters.map((flag) => ({
      queryKey: [NOTES_KEYS.clientNotes, clientId, flag, limit, authorRole ?? null],
      enabled: isEnabled,
      // A bigger page is a new key; the notes already on screen stay there while it loads.
      placeholderData: keepPreviousData,
      queryFn: () =>
        fetchClientNotes(clientId, {
          ...(flag === null ? {} : { flag }),
          ...(authorRole === undefined ? {} : { authorRole }),
          limit,
          offset: 0,
        }),
    })),
  });

  return {
    notes: results.flatMap((result) => result.data?.notes ?? []),
    notesTotal: results.reduce((sum, result) => sum + (result.data?.total ?? 0), 0),
    // Compared against what was asked for rather than against the rows in hand, so a
    // row the normaliser had to drop cannot leave a "load more" that never finishes.
    hasMoreNotes: results.some((result) => (result.data?.total ?? 0) > limit),
    areNotesLoading: results.some((result) => result.isLoading),
    areNotesFetching: results.some((result) => result.isFetching),
    hasNotesError: results.some((result) => result.isError),
    notesError: results.find((result) => result.isError)?.error ?? null,
  };
};

/** The notes on one visit, for the visit panel. */
export const useVisitNotes = (visitId: number, enabled = true) => {
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [NOTES_KEYS.visitNotes, visitId],
    enabled: enabled && Number.isFinite(visitId) && visitId > 0,
    queryFn: () => fetchVisitNotes(visitId, { limit: NOTES_PAGE_SIZE, offset: 0 }),
  });

  const list = data ?? EMPTY_LIST;

  return {
    notes: list.notes,
    notesTotal: list.total,
    // `notesTotal` falls back to 0 when nothing has arrived, so a caller that shows the
    // number needs to know whether the server has actually answered.
    areNotesLoaded: data !== undefined,
    areNotesLoading: isLoading,
    areNotesFetching: isFetching,
    hasNotesError: isError,
    notesError: error,
  };
};

/**
 * How many notes are addressed to the signed-in user and not yet read.
 *
 * `retry: false` and a long `staleTime` because this runs wherever the indicator is
 * mounted: while the endpoint does not exist, one failed request per mount is the
 * whole cost, and the indicator simply renders nothing.
 */
export const useUnreadNoteCount = (enabled = true) => {
  const { data, isError } = useQuery({
    queryKey: [NOTES_KEYS.unread],
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: fetchUnreadCount,
  });

  return { unread: isError ? 0 : (data?.unread ?? 0), hasUnreadError: isError };
};

/**
 * Write a note, and refresh both timelines it could appear in.
 *
 * Invalidation is by key prefix rather than by the exact key: a note written from
 * the visit panel belongs to the client's stream too, and the filter that stream is
 * currently under is not this hook's business.
 *
 * A null note means the server answered with a row that could not be read, so the
 * visit it was anchored to is unknown; every visit timeline is refreshed instead.
 */
export const useCreateNote = (clientId: number) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: CreateNotePayload) => createClientNote(clientId, payload),
    onSuccess: (note: Note | null) => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.clientNotes, clientId] });
      queryClient.invalidateQueries({
        queryKey: note?.visitId ? [NOTES_KEYS.visitNotes, note.visitId] : [NOTES_KEYS.visitNotes],
      });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.unread] });
    },
  });

  return {
    createNote: mutation.mutateAsync,
    isCreatingNote: mutation.isPending,
    createNoteError: mutation.error,
    resetCreateNote: mutation.reset,
  };
};

/** Edit a note's body (its author) or set its priority (an administrator). */
export const useUpdateNote = (clientId: number) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ noteId, payload }: { noteId: number; payload: UpdateNotePayload }) => updateNote(noteId, payload),
    onSuccess: (note: Note | null) => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.clientNotes, clientId] });
      queryClient.invalidateQueries({
        queryKey: note?.visitId ? [NOTES_KEYS.visitNotes, note.visitId] : [NOTES_KEYS.visitNotes],
      });
    },
  });

  return { updateNote: mutation.mutateAsync, isUpdatingNote: mutation.isPending, updateNoteError: mutation.error };
};

export const useDeleteNote = (clientId: number) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (noteId: number) => deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.clientNotes, clientId] });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.visitNotes] });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.unread] });
    },
  });

  return { removeNote: mutation.mutateAsync, isDeletingNote: mutation.isPending };
};

/**
 * Mark a batch read. Failure is silent by design — nothing the reader did is undone by it.
 *
 * The timelines are invalidated alongside the count: `isRead` is a field on the note
 * rows, so refreshing only the header badge left the blue dots and the panel's "N
 * unread" pill contradicting it until something else happened to refetch.
 */
export const useMarkNotesRead = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (noteIds: number[]) => markNotesRead(noteIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.clientNotes] });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.visitNotes] });
      queryClient.invalidateQueries({ queryKey: [NOTES_KEYS.unread] });
    },
  });

  return { markRead: mutation.mutate, isMarkingRead: mutation.isPending };
};
